import { Component, Input, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { CanvasComponent } from "../canvas/canvas.component";
import { ToolbarComponent } from "../toolbar/toolbar.component";
import { ImageGalleryComponent } from "../image-gallery/image-gallery.component";
import { SlideNavigatorComponent } from "../slide-navigator/slide-navigator.component";
import { CanvasService } from "../../services/canvas.service";

@Component({
  selector: "lib-canvas-designer",
  template: `
    <div class="canvas-designer">
      <div class="canvas-designer__header">
        <lib-toolbar></lib-toolbar>
      </div>

      <div class="canvas-designer__content">
        <!-- Fixed left sidebar for slides -->
        <div class="canvas-designer__sidebar canvas-designer__sidebar--left">
          <div class="canvas-designer__panel">
            <lib-slide-navigator></lib-slide-navigator>
          </div>
        </div>

        <div class="canvas-designer__main">
          <div class="canvas-designer__canvas-area">
            <lib-canvas [readonly]="readonly"></lib-canvas>
          </div>
        </div>

        <!-- Fixed right sidebar for images -->
        <div class="canvas-designer__sidebar canvas-designer__sidebar--right">
          <div class="canvas-designer__panel">
            <lib-image-gallery [imageUrls]="imageUrls"></lib-image-gallery>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      :host {
        /* Import CSS variables */
        --canvas-primary: #232459;
        --canvas-primary-50: #e3e3ed;
        --canvas-primary-dark: #14155e;
        --canvas-accent: #706fd3;
        --canvas-gray-50: #f9fafb;
        --canvas-gray-100: #f3f4f6;
        --canvas-gray-700: #374151;
        --canvas-bg-primary: #ffffff;
        --canvas-bg-secondary: #f8fafc;
        --canvas-bg-tertiary: #f1f5f9;
        --canvas-border-light: #e2e8f0;
        --canvas-space-2: 0.5rem;
        --canvas-space-3: 0.75rem;
        --canvas-space-4: 1rem;
        --canvas-text-sm: 0.875rem;
        --canvas-font-medium: 500;
        --canvas-font-family: "Google Sans", -apple-system, BlinkMacSystemFont,
          "Segoe UI", Roboto, sans-serif;
        --canvas-radius: 0.375rem;
        --canvas-radius-lg: 0.75rem;
        --canvas-transition: 200ms ease;
        --canvas-shadow-lg: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
        --canvas-z-dropdown: 1000;
        --canvas-z-fixed: 1030;
        --canvas-z-sticky: 1020;
        display: block;
        width: 100%;
        height: 100%;
      }

      .canvas-designer {
        height: 100%;
        display: flex;
        flex-direction: column;
        background: #f8fafc;
        font-family: "Google Sans", -apple-system, BlinkMacSystemFont,
          "Segoe UI", Roboto, sans-serif;
        overflow-x: hidden;
        overflow-y: auto;
      }

      .canvas-designer__header {
        flex-shrink: 0;
        z-index: 1020;
        overflow: visible;
        position: relative;
      }

      .canvas-designer__content {
        flex: 1;
        display: flex;
        overflow: hidden;
        position: relative;
      }

      .canvas-designer__sidebar {
        display: flex;
        flex-direction: column;
        background: white;
        transition: all 200ms ease;
        z-index: 1000;
      }

      .canvas-designer__sidebar--left {
        width: 200px;
        border-right: 1px solid #e2e8f0;
      }

      .canvas-designer__sidebar--right {
        width: 180px;
        border-left: 1px solid #e2e8f0;
      }

      .canvas-designer__panel {
        flex: 1;
        overflow: hidden;
      }

      .canvas-designer__main {
        flex: 1;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        background: #f1f5f9;
      }

      .canvas-designer__canvas-area {
        flex: 1;
        overflow: auto;
        padding: 1rem;
        display: flex;
        align-items: flex-start;
        justify-content: flex-start;
        box-sizing: border-box;
      }

      /* Mobile Responsive */
      @media (max-width: 640px) {
        .canvas-designer__sidebar--left {
          width: 160px;
        }

        .canvas-designer__sidebar--right {
          width: 140px;
        }

        .canvas-designer__canvas-area {
          padding: 0.5rem;
        }
      }

      /* Tablet Responsive */
      @media (min-width: 641px) and (max-width: 768px) {
        .canvas-designer__sidebar--left {
          width: 180px;
        }

        .canvas-designer__sidebar--right {
          width: 160px;
        }

        .canvas-designer__canvas-area {
          padding: 0.75rem;
        }
      }

      /* Desktop */
      @media (min-width: 769px) {
        .canvas-designer__sidebar--left {
          width: 200px;
        }

        .canvas-designer__sidebar--right {
          width: 180px;
        }
      }
    `,
  ],
})
export class CanvasDesignerComponent implements OnInit {
  @Input() imageUrls: string[] = [
    "https://images.pexels.com/photos/1779487/pexels-photo-1779487.jpeg?auto=compress&cs=tinysrgb&w=400",
    "https://images.pexels.com/photos/1462935/pexels-photo-1462935.jpeg?auto=compress&cs=tinysrgb&w=400",
    "https://images.pexels.com/photos/1323550/pexels-photo-1323550.jpeg?auto=compress&cs=tinysrgb&w=400",
    "https://images.pexels.com/photos/1563356/pexels-photo-1563356.jpeg?auto=compress&cs=tinysrgb&w=400",
    "https://images.pexels.com/photos/1587927/pexels-photo-1587927.jpeg?auto=compress&cs=tinysrgb&w=400",
    "https://images.pexels.com/photos/1366919/pexels-photo-1366919.jpeg?auto=compress&cs=tinysrgb&w=400",
    "https://images.pexels.com/photos/1687845/pexels-photo-1687845.jpeg?auto=compress&cs=tinysrgb&w=400",
    "https://images.pexels.com/photos/1624438/pexels-photo-1624438.jpeg?auto=compress&cs=tinysrgb&w=400",
  ];
  @Input() readonly: boolean = false;

  constructor(private canvasService: CanvasService) {}

  ngOnInit(): void {
    // Only create a default project if no project is currently loaded
    setTimeout(() => {
      this.canvasService.createProjectIfNotExists("My Canvas Project");
    }, 100); // Small delay to allow parent component to load data first
  }
}
